import React, { useEffect, useMemo, useState, useCallback, memo } from "react";
import { FIDIC_CPA_TABLES } from "./CPA";
import { AiInteractiveHelperButton } from "./AIHelper";
import { useDebouncedValue } from "./useDebouncedValue";
import { tokens } from "./theme";
import {
  Card as UICard,
  BlueprintGrid,
  Reveal,
  Eyebrow,
  Input as UIInput,
  Select as UISelect,
  Button as UIButton,
  Badge as UIBadge,
  Checkbox as UICheckbox,
  Stat,
  Table as UITable,
  Th as UITh,
  Td as UITd,
  Empty,
} from "./ui";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";

const T = tokens;

// ─────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────
const DS = {
  blue:      "#1D6FE8",
  blueSoft:  "#E8F0FD",
  teal:      "#0E8C6A",
  tealSoft:  "#E5F5F0",
  amber:     "#D08B20",
  amberSoft: "#FDF4E3",
  purple:    "#6D3BB5",
  purpleSoft:"#F0EBF8",
  red:       "#C9352A",
  redSoft:   "#FBE9E8",
  muted:     "#64748B",
  border:    "#E2E8F2",
  surface:   "#F8FAFC",
  white:     "#FFFFFF",
  text:      "#0F172A",
  r8:  "8px", r12: "12px", r16: "16px", r20: "20px",
  shadowSm: "0 1px 4px rgba(15,23,42,0.07)",
};

// ─────────────────────────────────────────────
// UTILITIES  (all logic identical to original)
// ─────────────────────────────────────────────
function numberOrZero(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value || "").replace(/R/gi, "").replace(/,/g, "").trim();
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function round2(value) {
  return Math.round(numberOrZero(value) * 100) / 100;
}

// Quantity the engineer's estimate is priced on. When the user has NOT entered a
// final estimated qty, the value defaults to ZERO — an unestimated item carries no
// engineer's-estimate value until a quantity is entered (an explicit 0 is also 0).
// This is the single rule used by the register display, the exported totals, and
// the section breakdown, so the SOQ Register and Financial Dashboard stay in sync.
function effectiveFinalQty(finalEstimatedQty) {
  return numberOrZero(finalEstimatedQty);
}

function fmtCurrency(value) {
  return `R ${round2(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function normalizeItemNo(value) {
  return String(value || "").trim().toUpperCase();
}

function isApprovedVariation(item) {
  return String(item?.status || "").trim().toLowerCase() === "approved";
}

function getOmissionAllocationsForItem(itemNo, variations) {
  const norm = normalizeItemNo(itemNo);
  return (variations || [])
    .filter(isApprovedVariation)
    .flatMap((v) =>
      (Array.isArray(v.omissionItems) ? v.omissionItems : [])
        .filter((e) => normalizeItemNo(e.itemNo) === norm)
        .map((e) => ({
          variationId:   v.id || "",
          variationNo:   v.variationNo || v.id || "—",
          omitQty:       round2(e.omitQty),
          omittedAmount: round2(e.omittedAmount),
        }))
    );
}

function getCertificateConsumptionsForItem(itemNo, certificates) {
  const norm = normalizeItemNo(itemNo);
  return (certificates || []).flatMap((cert) =>
    (Array.isArray(cert.claimedItems) ? cert.claimedItems : [])
      .filter((e) => normalizeItemNo(e.itemNo) === norm)
      .map((e) => ({
        certificateId:   cert.id || "",
        certificateNo:   cert.certificateNo || cert.id || "—",
        certificateDate: cert.certificateDate || "",
        thisMonthQty:    round2(e.thisMonthQty),
        cumulativeQty:   round2(e.cumulativeQty),
        amountThisMonth: round2(e.amountThisMonth),
      }))
  );
}

// ─────────────────────────────────────────────
// SHARED UI PRIMITIVES
// ─────────────────────────────────────────────
function Badge({ children, variant = "default" }) {
  const map = {
    default: { bg: DS.surface,     color: DS.muted,   border: DS.border   },
    info:    { bg: DS.blueSoft,    color: DS.blue,    border: "#BDD3F9"   },
    success: { bg: DS.tealSoft,    color: DS.teal,    border: "#9FDACB"   },
    warning: { bg: DS.amberSoft,   color: DS.amber,   border: "#F0CFA0"   },
    danger:  { bg: DS.redSoft,     color: DS.red,     border: "#F3C4C1"   },
    purple:  { bg: DS.purpleSoft,  color: DS.purple,  border: "#C8B5E8"   },
  };
  const s = map[variant] || map.default;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "2px 9px",
      borderRadius: 999, fontSize: 11, fontWeight: 600,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

function Btn({ children, onClick, variant = "primary", size = "md", disabled, type = "button", fullWidth }) {
  const base = {
    border: "none", borderRadius: DS.r8, fontWeight: 600, cursor: "pointer",
    fontFamily: "inherit", transition: "all 0.15s", lineHeight: 1,
    padding: size === "sm" ? "6px 12px" : "9px 16px",
    fontSize: size === "sm" ? 12 : 13,
    opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? "none" : "auto",
    width: fullWidth ? "100%" : undefined,
  };
  const variants = {
    primary:   { background: DS.blue,    color: "#fff" },
    secondary: { background: DS.surface, color: DS.text, border: `1px solid ${DS.border}` },
    success:   { background: DS.teal,    color: "#fff" },
    danger:    { background: DS.red,     color: "#fff" },
    purple:    { background: DS.purple,  color: "#fff" },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ ...base, ...variants[variant] || variants.primary }}>
      {children}
    </button>
  );
}

function SectionCard({ title, subtitle, children, action, accent }) {
  return (
    <div style={{
      background: DS.white, border: `1px solid ${DS.border}`,
      borderRadius: DS.r20, boxShadow: DS.shadowSm, overflow: "hidden", marginBottom: 14,
      ...(accent ? { borderTop: `3px solid ${accent}` } : {}),
    }}>
      <div style={{
        padding: "13px 20px", borderBottom: `1px solid ${DS.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: DS.text }}>{title}</h3>
          {subtitle && <p style={{ margin: "2px 0 0", fontSize: 11, color: DS.muted }}>{subtitle}</p>}
        </div>
        {action}
      </div>
      <div style={{ padding: "16px 20px" }}>{children}</div>
    </div>
  );
}

function KpiCard({ label, value, sub, accent, valueColor }) {
  return (
    <div style={{
      background: DS.white, border: `1px solid ${DS.border}`,
      borderRadius: DS.r12, padding: "12px 14px",
      boxShadow: DS.shadowSm, borderTop: `3px solid ${accent || DS.blue}`,
      display: "grid", gap: 3,
    }}>
      <p style={{ margin: 0, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.08em", color: DS.muted }}>{label}</p>
      <p style={{ margin: 0, fontSize: 18, fontWeight: 700,
        letterSpacing: "-0.02em", color: valueColor || DS.text }}>{value}</p>
      {sub && <p style={{ margin: 0, fontSize: 11, color: DS.muted }}>{sub}</p>}
    </div>
  );
}

// Shared input style
const inp = {
  width: "100%", boxSizing: "border-box",
  border: `1px solid ${DS.border}`, borderRadius: DS.r8,
  padding: "7px 10px", fontSize: 12, color: DS.text,
  background: DS.white, fontFamily: "inherit",
};

// Shared label style
const lbl = {
  display: "block", marginBottom: 4, fontSize: 10, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: "0.07em", color: DS.muted,
};

// Table header cell
function TH({ children, sticky, left, right, width }) {
  return (
    <th style={{
      padding: "8px 10px", textAlign: right ? "right" : "left",
      fontSize: 10, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: "0.07em", color: DS.muted,
      borderBottom: `2px solid ${DS.border}`,
      background: DS.surface, whiteSpace: "nowrap",
      ...(sticky ? { position: "sticky", top: 0, zIndex: left ? 4 : 3 } : {}),
      ...(left  !== undefined ? { position: "sticky", left,  zIndex: 2, background: DS.surface } : {}),
      ...(width ? { width, minWidth: width } : {}),
    }}>{children}</th>
  );
}

// ─────────────────────────────────────────────
// EXCEL IMPORT MODAL
// ─────────────────────────────────────────────
function FinalQtyImportModal({ onClose, onApply, projectItems }) {
  const [pasteText,     setPasteText]     = useState("");
  const [matchColumn,   setMatchColumn]   = useState("");
  const [valueColumn,   setValueColumn]   = useState("");
  const [message,       setMessage]       = useState("");

  const importRows = useMemo(() => {
    const text = String(pasteText || "").trim();
    if (!text) return [];
    return text
      .split(/\r?\n/)
      .map((line) => line.split("\t").map((cell) => String(cell || "").trim()))
      .filter((row) => row.some((c) => c !== ""));
  }, [pasteText]);

  const headers     = useMemo(() => importRows.length ? importRows[0].map((h, i) => h || `Column ${i + 1}`) : [], [importRows]);
  const previewRows = useMemo(() => importRows.slice(1, 6), [importRows]);

  function handleApply() {
    const matchIdx = headers.indexOf(matchColumn);
    const qtyIdx   = headers.indexOf(valueColumn);
    if (matchIdx < 0 || qtyIdx < 0) {
      setMessage("Select both the item-number column and the final estimated quantity column.");
      return;
    }
    const itemMap = new Map(
      projectItems.map((item) => [normalizeItemNo(item.itemNo), item]).filter(([k]) => k)
    );
    let updated = 0, skipped = 0;
    const updates = {};
    importRows.slice(1).forEach((row) => {
      const itemNo = normalizeItemNo(row[matchIdx]);
      const qty    = numberOrZero(row[qtyIdx]);
      const match  = itemMap.get(itemNo);
      if (!itemNo || !match) { skipped++; return; }
      updates[match.id] = qty;
      updated++;
    });
    onApply(updates);
    setMessage(`Updated ${updated} SOQ item${updated !== 1 ? "s" : ""}. Skipped ${skipped} row${skipped !== 1 ? "s" : ""}.`);
  }

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(11,22,40,0.58)",
        display: "grid", placeItems: "center", padding: 20, zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(880px, 96vw)", maxHeight: "90vh", overflowY: "auto",
          background: DS.white, borderRadius: DS.r20, padding: "0 0 24px",
          boxShadow: "0 24px 80px rgba(15,23,42,0.25)",
          border: `1px solid ${DS.border}`,
        }}
      >
        {/* Header */}
        <div style={{
          padding: "16px 22px", borderBottom: `1px solid ${DS.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          position: "sticky", top: 0, background: DS.white, zIndex: 1,
          borderRadius: `${DS.r20} ${DS.r20} 0 0`,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
              Update Final Estimated Qty from Excel
            </h3>
            <p style={{ margin: "3px 0 0", fontSize: 11, color: DS.muted }}>
              Copy the relevant columns from Excel and paste below. Select the item-number and quantity columns, then apply.
            </p>
          </div>
          <Btn variant="secondary" size="sm" onClick={onClose}>✕ Close</Btn>
        </div>

        <div style={{ padding: "20px 22px", display: "grid", gap: 16 }}>
          {/* Paste area */}
          <div>
            <label style={lbl}>Paste Excel Rows (Ctrl+V from spreadsheet)</label>
            <textarea
              value={pasteText}
              onChange={(e) => { setPasteText(e.target.value); setMessage(""); }}
              placeholder={"Item No\tFinal Qty\n1.1\t25\n1.2\t40"}
              rows={7}
              style={{ ...inp, fontFamily: "'JetBrains Mono','Fira Code',monospace", fontSize: 11, lineHeight: 1.6, resize: "vertical" }}
            />
          </div>

          {/* Column selectors */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end" }}>
            <div>
              <label style={lbl}>Match SOQ Item Column (Item No)</label>
              <select style={inp} value={matchColumn} onChange={(e) => setMatchColumn(e.target.value)}>
                <option value="">Select column…</option>
                {headers.map((h) => <option key={`m-${h}`} value={h}>{h}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Final Estimated Qty Column</label>
              <select style={inp} value={valueColumn} onChange={(e) => setValueColumn(e.target.value)}>
                <option value="">Select column…</option>
                {headers.map((h) => <option key={`v-${h}`} value={h}>{h}</option>)}
              </select>
            </div>
            <Btn
              variant="success"
              disabled={!importRows.length || !matchColumn || !valueColumn}
              onClick={handleApply}
            >
              Apply Updates
            </Btn>
          </div>

          {/* Feedback message */}
          {message && (
            <div style={{
              padding: "9px 14px", borderRadius: DS.r8, fontWeight: 600, fontSize: 12,
              background: message.startsWith("Updated") ? DS.tealSoft : DS.amberSoft,
              color: message.startsWith("Updated") ? DS.teal : DS.amber,
              border: `1px solid ${message.startsWith("Updated") ? "#9FDACB" : "#F0CFA0"}`,
            }}>
              {message.startsWith("Updated") ? "✓ " : "⚠ "}{message}
            </div>
          )}

          {/* Preview table */}
          {previewRows.length > 0 && (
            <div>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: DS.muted,
                textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Preview (first 5 data rows)
              </p>
              <div style={{ overflowX: "auto", border: `1px solid ${DS.border}`, borderRadius: DS.r8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr>
                      {headers.map((h) => (
                        <th key={h} style={{
                          padding: "6px 10px", textAlign: "left", fontWeight: 700,
                          fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em",
                          color: DS.muted, borderBottom: `2px solid ${DS.border}`,
                          background: DS.surface, whiteSpace: "nowrap",
                          ...(h === matchColumn ? { background: DS.blueSoft, color: DS.blue } : {}),
                          ...(h === valueColumn  ? { background: DS.tealSoft, color: DS.teal } : {}),
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, ri) => (
                      <tr key={ri} style={{ borderBottom: `1px solid ${DS.border}` }}>
                        {headers.map((h, ci) => (
                          <td key={ci} style={{
                            padding: "6px 10px",
                            ...(h === matchColumn ? { background: DS.blueSoft } : {}),
                            ...(h === valueColumn  ? { background: DS.tealSoft } : {}),
                          }}>{row[ci] || ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ margin: "6px 0 0", fontSize: 10, color: DS.muted }}>
                Blue column = item number match · Teal column = quantity to import
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SHARED UTILITY — used by FinancialDashboard
// ─────────────────────────────────────────────
/**
 * Computes the total engineer's estimate from raw SOQ items for a given project.
 * Formula: sum of (finalEstimatedQty ?? tenderQty) * tenderRate across all items.
 * This is the single source of truth — FinancialDashboardSection imports this
 * instead of receiving a separate engineersEstimateTotal prop.
 */
export function computeEngineersEstimateTotal(soqItems = [], projectId = "") {
  return (soqItems || [])
    .filter((i) => !projectId || i.projectId === projectId)
    .reduce((sum, item) => {
      const qty = effectiveFinalQty(item.finalEstimatedQty);
      return sum + Math.round(qty * numberOrZero(item.tenderRate) * 100) / 100;
    }, 0);
}

// Per-item tendered amount: prefer the stored tenderAmount, else tenderQty × rate.
function itemTenderAmount(item) {
  const stored = item?.tenderAmount;
  if (stored !== undefined && stored !== null && stored !== "") return round2(stored);
  return round2(numberOrZero(item?.tenderQty) * numberOrZero(item?.tenderRate));
}

/**
 * Total tendered value from raw SOQ items for a project — the tender baseline.
 * Single source of truth for the Financial Dashboard's "Tendered BOQ" figure.
 */
export function computeTenderedTotal(soqItems = [], projectId = "") {
  return (soqItems || [])
    .filter((i) => !projectId || i.projectId === projectId)
    .reduce((sum, item) => sum + itemTenderAmount(item), 0);
}

/**
 * Per-section tendered value vs engineer's estimate from raw SOQ items.
 * EE uses the same effectiveFinalQty rule, so section totals sum to
 * computeEngineersEstimateTotal and the dashboard section bars reconcile
 * with the SOQ Register. Returns rows sorted by section number/name.
 */
export function computeSectionBreakdown(soqItems = [], projectId = "") {
  const map = new Map();
  (soqItems || [])
    .filter((i) => !projectId || i.projectId === projectId)
    .forEach((item) => {
      const sectionNo = String(item.sectionNo || "").trim();
      const sectionName = String(item.sectionName || "").trim();
      const key = `${sectionNo}||${sectionName}`;
      if (!map.has(key)) {
        map.set(key, { id: key, sectionNo, sectionName, tenderedAmount: 0, engineersEstimate: 0, itemCount: 0 });
      }
      const entry = map.get(key);
      const ee = round2(effectiveFinalQty(item.finalEstimatedQty) * numberOrZero(item.tenderRate));
      entry.tenderedAmount += itemTenderAmount(item);
      entry.engineersEstimate += ee;
      entry.itemCount += 1;
    });
  return Array.from(map.values())
    .map((e) => ({
      ...e,
      tenderedAmount: round2(e.tenderedAmount),
      engineersEstimate: round2(e.engineersEstimate),
    }))
    .sort((a, b) =>
      String(a.sectionNo || a.sectionName).localeCompare(
        String(b.sectionNo || b.sectionName),
        undefined, { numeric: true, sensitivity: "base" }
      )
    );
}

// ─────────────────────────────────────────────
// SUBTOTAL ROW AMOUNT — small currency badge
// ─────────────────────────────────────────────
function SubtotalAmount({ label, value, accent }) {
  return (
    <div style={{
      display: "grid", gap: 1, textAlign: "right",
      minWidth: 110,
    }}>
      <span style={{
        fontSize: 10, color: DS.muted, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.4,
      }}>{label}</span>
      <strong style={{
        fontSize: 14, fontWeight: 800,
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        color: accent || DS.text,
      }}>{fmtCurrency(value)}</strong>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
function SOQRegisterSection({
  currentProjectId,
  soqItems = [],
  variations = [],
  certificates = [],
  cpaClasses = [],
  handleUpdateSoqItem,
  handleCreateCpaClass,
  handleDeleteCpaClass,
}) {
  // ── Filter / sort state ──────────────────────
  const [searchText,     setSearchText]     = useState("");
  const [sectionFilter,  setSectionFilter]  = useState("All");
  const [cpaClassFilter,  setCpaClassFilter]  = useState("All");
  const [savingsFilter,   setSavingsFilter]   = useState("All");
  const [sortBy,          setSortBy]          = useState("itemNo_asc");

  // ── Bulk / CPA class state ───────────────────
  const [selectedRowIds,   setSelectedRowIds]   = useState([]);
  const [bulkCpaClassId,   setBulkCpaClassId]   = useState("");
  const [newCpaClassName,  setNewCpaClassName]  = useState("");
  const [newCpaTableLink,  setNewCpaTableLink]  = useState("");

  // ── Final estimated qty state ────────────────
  const [estimatedQtyByItem, setEstimatedQtyByItem] = useState({});
  const [showImportModal,    setShowImportModal]    = useState(false);

  // ── Expanded "Used By" rows ──────────────────
  const [expandedRows, setExpandedRows] = useState({});

  // ── Subtotal markers ─────────────────────────
  // A "marker" is just a row id — when present, a subtotal row is rendered
  // immediately *after* that row, summing every item from the previous marker
  // (or top of register) down to and including it. Markers are stored as a
  // Set of row ids. Labels are kept per-marker so users can rename groups.
  const [subtotalMarkers, setSubtotalMarkers] = useState([]);
    // [{ id: rowId, label: "Subtotal 1" }, ...]
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [isolatedSubtotalId, setIsolatedSubtotalId] = useState(null);
    // when set, the table only shows the group ending at this subtotal

  // CPA table options (unchanged from original)
  const cpaTableOptions = useMemo(() => {
    const fidicOptions = FIDIC_CPA_TABLES.map((t) => ({
      value: t.key, label: t.title, formula: t.formula,
    }));
    return [
      { value: "gcc_default", label: "GCC Default CPA Table", formula: "GCC CPA formula" },
      ...fidicOptions,
    ];
  }, []);

  // Sync finalEstimatedQty from props into local state
  useEffect(() => {
    setEstimatedQtyByItem(
      (soqItems || []).reduce((acc, item) => {
        acc[item.id] =
          item.finalEstimatedQty === undefined || item.finalEstimatedQty === null
            ? "" : String(item.finalEstimatedQty);
        return acc;
      }, {})
    );
  }, [soqItems, currentProjectId]);

  // Project-scoped items
  const projectItems = useMemo(
    () => (soqItems || []).filter((i) => i.projectId === currentProjectId),
    [soqItems, currentProjectId]
  );

  // Merged CPA classes (from props + inferred from items)
  const projectCpaClasses = useMemo(() => {
    const fromProps = (cpaClasses || []).filter(
      (i) => !i.projectId || i.projectId === currentProjectId
    );
    const fromItems = projectItems
      .map((i) => ({
        id: i.cpaClassId || "", name: i.cpaClassName || i.cpaClassId || "",
        linkedCpaTableId: i.linkedCpaTableId || "", projectId: currentProjectId, active: true,
      }))
      .filter((i) => i.id || i.name);

    const byId = new Map();
    [...fromProps, ...fromItems].forEach((item) => {
      const id   = String(item.id || item.name || "").trim();
      const name = String(item.name || item.id || "").trim();
      if (!id && !name) return;
      const safeId = id || name.toLowerCase().replace(/[^a-z0-9]+/gi, "_");
      byId.set(safeId, {
        id: safeId, name: name || safeId,
        linkedCpaTableId: item.linkedCpaTableId || "",
        projectId: item.projectId || currentProjectId,
        active: item.active !== false,
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true, sensitivity: "base" })
    );
  }, [cpaClasses, projectItems, currentProjectId]);

  const sectionOptions = useMemo(() => {
    const unique = Array.from(new Set(
      projectItems.map((i) => `${i.sectionNo || ""} - ${i.sectionName || ""}`.trim()).filter(Boolean)
    )).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
    return ["All", ...unique];
  }, [projectItems]);

  // ── Enriched + filtered rows ─────────────────
  // Debounce the free-text search so every keystroke doesn't re-filter
  // the whole table. 180ms feels instant but skips ~80% of intermediate work.
  const debouncedSearch = useDebouncedValue(searchText, 180);

  // ── HEAVY enrichment: only recomputes when underlying data changes ─────
  // Previously this was fused into `filteredRows` which meant every keystroke
  // in search or qty inputs re-ran the entire enrichment over every SOQ item.
  // Splitting it lets filters/sort/search be near-free.
  const enrichedRows = useMemo(() => {
    return projectItems.map((item) => {
      const omissionAllocations     = getOmissionAllocationsForItem(item.itemNo, variations);
      const certificateConsumptions = getCertificateConsumptionsForItem(item.itemNo, certificates);
      const allocatedVoQty    = round2(omissionAllocations.reduce((s, e) => s + numberOrZero(e.omitQty), 0));
      const allocatedVoAmount = round2(omissionAllocations.reduce((s, e) => s + numberOrZero(e.omittedAmount), 0));
      const certifiedQty      = round2(certificateConsumptions.reduce((s, e) => s + numberOrZero(e.thisMonthQty), 0));
      const certifiedAmount   = round2(certificateConsumptions.reduce((s, e) => s + numberOrZero(e.amountThisMonth), 0));
      const liveOverride = estimatedQtyByItem[item.id];
      const hasLiveOverride = liveOverride !== undefined && liveOverride !== "";
      // Final qty: the entered value, or 0 when nothing has been entered. The
      // engineer's estimate for an unestimated row is therefore R0 until a qty is set.
      const finalEstimatedQty = hasLiveOverride
        ? numberOrZero(liveOverride)
        : numberOrZero(item.finalEstimatedQty);
      const finalEngineersEstimateAmount = round2(finalEstimatedQty * numberOrZero(item.tenderRate));
      const remainingQty   = round2(numberOrZero(item.tenderQty) - allocatedVoQty - certifiedQty);
      const remainingValue = round2(remainingQty * numberOrZero(item.tenderRate));
      return {
        ...item, finalEstimatedQty, finalEngineersEstimateAmount,
        allocatedVoQty, allocatedVoAmount, certifiedQty, certifiedAmount,
        remainingQty, remainingValue, omissionAllocations, certificateConsumptions,
      };
    });
  }, [projectItems, variations, certificates, estimatedQtyByItem]);

  // ── CHEAP filter + sort over already-enriched rows ─────────────────────
  const filteredRows = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    const f = enrichedRows.filter((item) => {
      const sectionLabel = `${item.sectionNo || ""} - ${item.sectionName || ""}`.trim();
      if (sectionFilter !== "All" && sectionLabel !== sectionFilter) return false;
      if (cpaClassFilter === "__unassigned__") {
        if (item.cpaClassId && String(item.cpaClassId).trim() !== "") return false;
      } else if (cpaClassFilter !== "All") {
        if (String(item.cpaClassId) !== cpaClassFilter) return false;
      }
      if (savingsFilter === "savings"     && !item.identifiedSaving) return false;
      if (savingsFilter === "non-savings" &&  item.identifiedSaving) return false;
      if (!needle) return true;
      const hay = [item.itemNo, item.paymentRef, item.description, item.unit,
        item.sectionNo, item.sectionName, item.cpaClassName, item.cpaClassId].join(" ").toLowerCase();
      return hay.includes(needle);
    });

    return [...f].sort((a, b) => {
      if (sortBy === "itemNo_desc")        return String(b.itemNo || "").localeCompare(String(a.itemNo || ""), undefined, { numeric: true, sensitivity: "base" });
      if (sortBy === "value_desc")         return numberOrZero(b.tenderAmount) - numberOrZero(a.tenderAmount);
      if (sortBy === "value_asc")          return numberOrZero(a.tenderAmount) - numberOrZero(b.tenderAmount);
      if (sortBy === "remaining_qty_desc") return numberOrZero(b.remainingQty) - numberOrZero(a.remainingQty);
      if (sortBy === "remaining_qty_asc")  return numberOrZero(a.remainingQty) - numberOrZero(b.remainingQty);
      return String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true, sensitivity: "base" });
    });
  }, [enrichedRows, debouncedSearch, sectionFilter, cpaClassFilter, savingsFilter, sortBy]);

  // ── Subtotal grouping ─────────────────────────
  // Walk filteredRows in display order and segment them into groups based on
  // subtotalMarkers. Each group has its accumulated totals, the list of row
  // ids it contains, and a reference to the marker that closes it (if any).
  const subtotalGroups = useMemo(() => {
    const markerIds = new Set(subtotalMarkers.map((m) => m.id));
    const labelLookup = Object.fromEntries(subtotalMarkers.map((m) => [m.id, m.label]));
    const groups = [];
    let current = {
      rowIds: [],
      tenderAmount: 0,
      engineersEstimate: 0,
      certifiedAmount: 0,
      markerRowId: null,   // row id where subtotal sits (null if open-ended group at end)
      label: "",
      isClosed: false,
    };

    filteredRows.forEach((row) => {
      current.rowIds.push(row.id);
      current.tenderAmount      += numberOrZero(row.tenderAmount);
      current.engineersEstimate += numberOrZero(row.finalEngineersEstimateAmount);
      current.certifiedAmount   += numberOrZero(row.certifiedAmount);

      if (markerIds.has(row.id)) {
        current.markerRowId = row.id;
        current.label = labelLookup[row.id] || `Subtotal ${groups.length + 1}`;
        current.isClosed = true;
        groups.push(current);
        current = {
          rowIds: [],
          tenderAmount: 0,
          engineersEstimate: 0,
          certifiedAmount: 0,
          markerRowId: null,
          label: "",
          isClosed: false,
        };
      }
    });

    // Trailing open group (no subtotal marker at the end) — keep as a group
    // for the isolation filter but it gets no subtotal row rendered.
    if (current.rowIds.length > 0) {
      groups.push(current);
    }

    return groups;
  }, [filteredRows, subtotalMarkers]);

  // Apply isolation filter on top of grouping. When an isolated subtotal id
  // is set, only the group whose markerRowId matches stays in view.
  const displayGroups = useMemo(() => {
    if (!isolatedSubtotalId) return subtotalGroups;
    return subtotalGroups.filter((g) => g.markerRowId === isolatedSubtotalId);
  }, [subtotalGroups, isolatedSubtotalId]);

  const isolatedGroupLabel = useMemo(() => {
    if (!isolatedSubtotalId) return "";
    const m = subtotalMarkers.find((x) => x.id === isolatedSubtotalId);
    return m?.label || "Isolated";
  }, [isolatedSubtotalId, subtotalMarkers]);

  // ── Subtotal handlers ─────────────────────────
  // ── Firestore sync for subtotal markers ──────
  // Markers live at projects/{currentProjectId}/soqSubtotalMarkers/{rowId}.
  // The document id matches the marker's rowId because we only ever allow one
  // marker per row. Marker rendering is purely a view-layer concern — no
  // financial calculations are derived from these documents, they exist only
  // to control how the register groups, displays subtotals, and isolates.
  useEffect(() => {
    if (!currentProjectId) {
      setSubtotalMarkers([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, "projects", currentProjectId, "soqSubtotalMarkers"),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort by stored `order` for stable rendering; fallback to id compare
        rows.sort((a, b) => {
          const ao = Number(a.order);
          const bo = Number(b.order);
          if (Number.isFinite(ao) && Number.isFinite(bo) && ao !== bo) return ao - bo;
          return String(a.id).localeCompare(String(b.id));
        });
        setSubtotalMarkers(rows.map((r) => ({ id: r.rowId || r.id, label: r.label || "", sectionNo: r.sectionNo || "", sectionName: r.sectionName || "", _docId: r.id })));
      },
      (err) => {
        console.warn("SOQ subtotal markers subscription failed", err);
      }
    );
    return () => unsub();
  }, [currentProjectId]);

  const addSubtotalAfter = useCallback(async (rowId) => {
    if (!currentProjectId || !rowId) return;
    // Already a marker on this row?
    if (subtotalMarkers.some((m) => m.id === rowId)) return;

    // Default label: number it based on the row's index in the current
    // filtered list so the auto-numbering matches what the user sees.
    const orderIndex = {};
    filteredRows.forEach((r, i) => { orderIndex[r.id] = i; });
    const sortedExisting = [...subtotalMarkers].sort(
      (a, b) => (orderIndex[a.id] ?? 0) - (orderIndex[b.id] ?? 0)
    );
    const insertionPos = sortedExisting.filter((m) => (orderIndex[m.id] ?? -1) < (orderIndex[rowId] ?? 0)).length;
    const defaultLabel = `Subtotal ${insertionPos + 1}`;

    try {
      await setDoc(
        doc(db, "projects", currentProjectId, "soqSubtotalMarkers", rowId),
        {
          rowId,
          label: defaultLabel,
          order: orderIndex[rowId] ?? Date.now(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (err) {
      console.error("Failed to save subtotal marker", err);
    }
  }, [currentProjectId, subtotalMarkers, filteredRows]);

  const removeSubtotal = useCallback(async (rowId) => {
    if (!currentProjectId || !rowId) return;
    try {
      await deleteDoc(doc(db, "projects", currentProjectId, "soqSubtotalMarkers", rowId));
    } catch (err) {
      console.error("Failed to delete subtotal marker", err);
    }
    if (isolatedSubtotalId === rowId) setIsolatedSubtotalId(null);
  }, [currentProjectId, isolatedSubtotalId]);

  // ── Sections (named subtotal markers) ─────────
  // A section IS a subtotal group with a name. Editing the number/name persists
  // it on the marker doc (cheap, one write per keystroke) and keeps `label` in
  // sync ("No — Name") so the isolation/label displays still read well.
  const setMarkerSection = useCallback(async (rowId, patch) => {
    if (!currentProjectId || !rowId) return;
    const existing = subtotalMarkers.find((m) => m.id === rowId) || {};
    const merged = { sectionNo: existing.sectionNo || "", sectionName: existing.sectionName || "", ...patch };
    const label = [merged.sectionNo, merged.sectionName]
      .map((x) => String(x || "").trim()).filter(Boolean).join(" — ") || existing.label || "";
    // Optimistic local update so the input stays responsive while Firestore round-trips.
    setSubtotalMarkers((prev) => prev.map((m) => (m.id === rowId ? { ...m, ...patch, label } : m)));
    try {
      await setDoc(
        doc(db, "projects", currentProjectId, "soqSubtotalMarkers", rowId),
        { rowId, ...patch, label, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error("Failed to save section on marker", err);
    }
  }, [currentProjectId, subtotalMarkers]);

  // Stamp a group's section onto every item in it (mirrors handleApplyBulkCpa).
  // Called on blur so we don't write N item docs on every keystroke. This is the
  // bridge that lets Variations/Certificates/CPA/Financial filter on item fields.
  const stampGroupSection = useCallback((group) => {
    if (typeof handleUpdateSoqItem !== "function" || !group || !group.markerRowId) return;
    const marker = subtotalMarkers.find((m) => m.id === group.markerRowId);
    if (!marker) return;
    const sectionNo = String(marker.sectionNo || "").trim();
    const sectionName = String(marker.sectionName || "").trim();
    (group.rowIds || []).forEach((id) => handleUpdateSoqItem(id, { sectionNo, sectionName }));
  }, [handleUpdateSoqItem, subtotalMarkers]);

  // Backfill: stamp every named section onto its items across the WHOLE bill —
  // ignores active filters and picks up newly-added items. Groups the full item
  // set in item-number order (the canonical order) segmented by section markers.
  const applyAllSections = useCallback(() => {
    if (typeof handleUpdateSoqItem !== "function") return;
    const markerById = {};
    subtotalMarkers.forEach((m) => { markerById[m.id] = m; });
    const sorted = [...projectItems].sort((a, b) =>
      String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true, sensitivity: "base" })
    );
    let bucket = [];
    let stamped = 0;
    sorted.forEach((item) => {
      bucket.push(item);
      const marker = markerById[item.id];
      if (marker) {
        const sectionNo = String(marker.sectionNo || "").trim();
        const sectionName = String(marker.sectionName || "").trim();
        if (sectionNo || sectionName) {
          bucket.forEach((it) => { handleUpdateSoqItem(it.id, { sectionNo, sectionName }); stamped += 1; });
        }
        bucket = [];
      }
    });
    window.alert(stamped
      ? `Applied sections to ${stamped} item${stamped === 1 ? "" : "s"}.`
      : "No named sections to apply yet — name a section on a subtotal row first.");
  }, [handleUpdateSoqItem, subtotalMarkers, projectItems]);

  const isolateSubtotal = useCallback((rowId) => {
    setIsolatedSubtotalId((prev) => (prev === rowId ? null : rowId));
  }, []);

  const clearAllSubtotals = useCallback(async () => {
    if (!currentProjectId || subtotalMarkers.length === 0) return;
    if (!window.confirm(`Remove all ${subtotalMarkers.length} subtotal marker${subtotalMarkers.length === 1 ? "" : "s"} from this project?`)) return;
    try {
      const batch = writeBatch(db);
      subtotalMarkers.forEach((m) => {
        const docId = m._docId || m.id;
        batch.delete(doc(db, "projects", currentProjectId, "soqSubtotalMarkers", docId));
      });
      await batch.commit();
    } catch (err) {
      console.error("Failed to clear subtotal markers", err);
    }
    setIsolatedSubtotalId(null);
  }, [currentProjectId, subtotalMarkers]);

  // ── Summary totals ────────────────────────────
  const summary = useMemo(() => {
    const totalTenderValue = filteredRows.reduce((s, i) => s + numberOrZero(i.tenderAmount), 0);

    // totalSavingsValue = full tender value of flagged items.
    // savingsAllocated  = approved VO omission amounts against those items.
    // savingsBalance    = what remains to be allocated via VOs.
    // (Do NOT use remainingValue here — that already subtracts allocations,
    //  which would double-deduct when savingsAllocated is then subtracted.)
    const savingsItems      = projectItems.filter((i) => i.identifiedSaving);
    const totalSavingsValue = round2(savingsItems.reduce((s, i) => s + numberOrZero(i.tenderAmount), 0));
    const savingsAllocated  = round2(savingsItems.reduce((s, i) => {
      const omissions = getOmissionAllocationsForItem(i.itemNo, variations);
      return s + omissions.reduce((ss, o) => ss + numberOrZero(o.omittedAmount), 0);
    }, 0));
    const savingsBalance    = round2(totalSavingsValue - savingsAllocated);

    return {
      itemCount:                   filteredRows.length,
      totalTenderValue,
      totalFinalEngineersEstimate: filteredRows.reduce((s, i) => s + numberOrZero(i.finalEngineersEstimateAmount), 0),
      totalSavingsValue,
      savingsAllocated,
      savingsBalance,
      savingsCount: savingsItems.length,
      totalAllocatedVoQty:         round2(filteredRows.reduce((s, i) => s + numberOrZero(i.allocatedVoQty), 0)),
      totalCertifiedQty:           round2(filteredRows.reduce((s, i) => s + numberOrZero(i.certifiedQty), 0)),
      totalRemainingQty:           round2(filteredRows.reduce((s, i) => s + numberOrZero(i.remainingQty), 0)),
      negativBalanceCount:         filteredRows.filter((i) => i.remainingQty < 0).length,
    };
  }, [filteredRows, projectItems, projectCpaClasses]);

  // ── Section breakdown: tendered value vs engineer's estimate per section ──
  // Built from the full enriched item set (unfiltered) so it always shows the
  // complete contract picture. EE per section uses the same effective-qty
  // figures as the rest of the register, so the section totals sum to the
  // headline Engineer's Estimate and reconcile with the Financial Dashboard.
  const sectionBreakdown = useMemo(() => {
    const map = new Map();
    enrichedRows.forEach((row) => {
      const sectionNo = String(row.sectionNo || "").trim();
      const sectionName = String(row.sectionName || "").trim();
      const key = `${sectionNo}||${sectionName}` || "__none__";
      if (!map.has(key)) {
        map.set(key, { key, sectionNo, sectionName, tenderValue: 0, engineersEstimate: 0, itemCount: 0 });
      }
      const entry = map.get(key);
      entry.tenderValue += numberOrZero(row.tenderAmount);
      entry.engineersEstimate += numberOrZero(row.finalEngineersEstimateAmount);
      entry.itemCount += 1;
    });
    return Array.from(map.values())
      .map((e) => ({
        ...e,
        tenderValue: round2(e.tenderValue),
        engineersEstimate: round2(e.engineersEstimate),
        variance: round2(e.engineersEstimate - e.tenderValue),
      }))
      .sort((a, b) =>
        String(a.sectionNo || a.sectionName).localeCompare(
          String(b.sectionNo || b.sectionName),
          undefined, { numeric: true, sensitivity: "base" }
        )
      );
  }, [enrichedRows]);

  // ── Selection helpers ─────────────────────────
  const visibleIds   = filteredRows.map((r) => r.id).filter(Boolean);
  const allSelected  = visibleIds.length > 0 && visibleIds.every((id) => selectedRowIds.includes(id));
  const toggleOne    = (id) => setSelectedRowIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAll    = () =>
    setSelectedRowIds(allSelected
      ? selectedRowIds.filter((id) => !visibleIds.includes(id))
      : Array.from(new Set([...selectedRowIds, ...visibleIds]))
    );

  // ── CPA handlers ──────────────────────────────
  function handleRowCpaChange(row, classId) {
    if (typeof handleUpdateSoqItem !== "function") return;
    const sel = projectCpaClasses.find((c) => String(c.id) === String(classId));
    handleUpdateSoqItem(row.id, {
      cpaClassId:       classId,
      cpaClassName:     sel?.name || "",
      linkedCpaTableId: sel?.linkedCpaTableId || "",
    });
  }

  function handleApplyBulkCpa() {
    if (typeof handleUpdateSoqItem !== "function" || !bulkCpaClassId || !selectedRowIds.length) return;
    const sel = projectCpaClasses.find((c) => String(c.id) === String(bulkCpaClassId));
    selectedRowIds.forEach((id) =>
      handleUpdateSoqItem(id, {
        cpaClassId:       bulkCpaClassId,
        cpaClassName:     sel?.name || "",
        linkedCpaTableId: sel?.linkedCpaTableId || "",
      })
    );
    setSelectedRowIds([]);
  }

  function handleAddCpaClass() {
    const name = newCpaClassName.trim();
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
    if (typeof handleCreateCpaClass === "function") {
      handleCreateCpaClass({ id, name, linkedCpaTableId: newCpaTableLink, projectId: currentProjectId, active: true });
    }
    setBulkCpaClassId(id);
    setNewCpaClassName("");
    setNewCpaTableLink("");
  }

  // ── Final estimated qty handlers ──────────────
  function handleEstimatedQtyChange(itemId, value) {
    setEstimatedQtyByItem((p) => ({ ...p, [itemId]: value }));
  }

  function handleEstimatedQtyBlur(row) {
    if (typeof handleUpdateSoqItem !== "function") return;
    handleUpdateSoqItem(row.id, { finalEstimatedQty: numberOrZero(estimatedQtyByItem[row.id]) });
  }

  function handleToggleSaving(row) {
    if (typeof handleUpdateSoqItem !== "function") return;
    handleUpdateSoqItem(row.id, { identifiedSaving: !row.identifiedSaving });
  }

  function handleImportApply(updates) {
    // updates = { [itemId]: qty }
    Object.entries(updates).forEach(([itemId, qty]) => {
      if (typeof handleUpdateSoqItem === "function") {
        handleUpdateSoqItem(itemId, { finalEstimatedQty: qty });
      }
    });
    setEstimatedQtyByItem((p) => {
      const next = { ...p };
      Object.entries(updates).forEach(([id, qty]) => { next[id] = String(qty); });
      return next;
    });
  }

  // ── Download the monthly-claim template (Phase 4) ─────────────────────────
  // Exports the project SOQ as an Excel the contractor fills in: item no /
  // description / unit / rate pre-filled + previous qty to date, with an empty
  // "Qty This Month" column. Because the item numbers come straight from the SOQ,
  // uploading the returned file matches ~100% in "Capture from contractor submission".
  const [templateBusy, setTemplateBusy] = useState(false);
  async function handleDownloadClaimTemplate() {
    if (!projectItems.length) { alert("This project has no SOQ items to export yet."); return; }
    setTemplateBusy(true);
    try {
      const XLSX = await import("xlsx");
      const sorted = [...projectItems].sort((a, b) =>
        String(a.itemNo || "").localeCompare(String(b.itemNo || ""), undefined, { numeric: true, sensitivity: "base" })
      );
      const header = ["Item No", "Description", "Unit", "Rate", "Previous Qty (to date)", "Qty This Month", "Cumulative Qty"];
      const aoa = [header];
      for (const item of sorted) {
        const consumptions = getCertificateConsumptionsForItem(item.itemNo, certificates);
        const previousQty = round2(consumptions.reduce((sum, c) => sum + numberOrZero(c.thisMonthQty), 0));
        aoa.push([
          item.itemNo || "",
          item.description || "",
          item.unit || "",
          numberOrZero(item.rate),
          previousQty,
          "",   // ← contractor fills this
          "",   // Cumulative Qty — formula added below
        ]);
      }
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      // Live Cumulative Qty formula = Previous + This Month, per data row.
      for (let r = 2; r <= aoa.length; r++) ws[`G${r}`] = { t: "n", f: `E${r}+F${r}` };
      ws["!cols"] = [{ wch: 12 }, { wch: 48 }, { wch: 8 }, { wch: 12 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
      ws["!freeze"] = { xSplit: 0, ySplit: 1 };
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Monthly Claim");
      XLSX.writeFile(wb, `Monthly Claim Template${currentProjectId ? ` - ${currentProjectId}` : ""}.xlsx`);
    } catch (e) {
      alert(`Could not build the template: ${e?.message || e}`);
    } finally {
      setTemplateBusy(false);
    }
  }

  // ── "Used By" expand toggle ───────────────────
  function toggleExpand(rowId) {
    setExpandedRows((p) => ({ ...p, [rowId]: !p[rowId] }));
  }

  // ── AI TOOL DEFINITIONS ───────────────────────
  const totalTenderValue = projectItems.reduce(
    (s, i) => s + numberOrZero(i.tenderRate) * numberOrZero(i.tenderQty), 0
  );
  const cpaBreakdownForAi = projectCpaClasses.map(c => {
    const count = projectItems.filter(i => String(i.cpaClassId) === String(c.id)).length;
    return `${c.name}(${count})`;
  }).join(", ");

  // Build a compact item list to send to the AI so it can reason about actual data
  const aiItemList = projectItems.map(i => ({
    itemNo:      i.itemNo || "",
    description: (i.description || "").slice(0, 60),
    cpaClassId:  i.cpaClassId  || "",
    cpaName:     i.cpaClassName || "",
  }));

  // Serialise for the system prompt — keep it tight
  const aiItemSummary = aiItemList
    .map(i => `${i.itemNo}|${i.description}|cpa:${i.cpaName || "NONE"}`)
    .join("\n");

  const aiTools = [
    {
      name: "bulk_update_cpa_class",
      description: `Bulk-update the CPA class for SOQ items. You have the FULL item list available in the system prompt — always use it to determine which items qualify. Key rules:
- noCpaOnly=true means ONLY update items where cpaName is currently NONE/empty
- fromItemNo / toItemNo filter by item number prefix (e.g. A100 to A200)
- keyword filters by description
- currentCpaName filters by current CPA class name
Never update items that already have a CPA class unless explicitly told to override.`,
      input_schema: {
        type: "object",
        properties: {
          cpaClassId:     { type: "string", description: "The id of the CPA class to assign. Must match an existing class id from the project." },
          fromItemNo:     { type: "string", description: "Only update items whose itemNo starts with or is >= this value. e.g. 'A' to get all A items." },
          toItemNo:       { type: "string", description: "Only update items whose itemNo is <= this value. Leave empty for no upper bound." },
          noCpaOnly:      { type: "boolean", description: "If true, ONLY update items that currently have NO CPA class assigned. Use this whenever the user says 'without a category', 'missing CPA', 'no category', 'unassigned' etc." },
          currentCpaName: { type: "string", description: "Only update items whose current CPA class name exactly matches this. Leave empty to ignore." },
          keyword:        { type: "string", description: "Only update items whose description contains this word/phrase." },
          specificItemNos:{ type: "array", items: { type: "string" }, description: "Optionally provide exact item numbers to update. Overrides other filters." },
        },
        required: ["cpaClassId"],
      },
      handler: async (input) => {
        const { cpaClassId, fromItemNo, toItemNo, noCpaOnly, currentCpaName, keyword, specificItemNos } = input;
        const targetClass = projectCpaClasses.find(c => String(c.id) === String(cpaClassId));

        const candidates = projectItems.filter(item => {
          const no  = String(item.itemNo || "").trim();
          const noL = no.toLowerCase();

          // Specific item list overrides everything else
          if (specificItemNos && specificItemNos.length > 0) {
            return specificItemNos.map(s => s.toLowerCase()).includes(noL);
          }

          // Item number range filter — use numeric-aware localeCompare so that
          // e.g. A30 < A210 < A580 < A2100 sorts correctly instead of lexicographically
          const rangeOpts = { numeric: true, sensitivity: "base" };
          if (fromItemNo && no.localeCompare(fromItemNo, undefined, rangeOpts) < 0) return false;
          if (toItemNo   && no.localeCompare(toItemNo,   undefined, rangeOpts) > 0) return false;

          // Only items with no CPA
          if (noCpaOnly && item.cpaClassId && String(item.cpaClassId).trim() !== "") return false;

          // Only items with a specific current CPA name
          if (currentCpaName && (item.cpaClassName || "").toLowerCase() !== currentCpaName.toLowerCase()) return false;

          // Description keyword
          if (keyword && !String(item.description || "").toLowerCase().includes(keyword.toLowerCase())) return false;

          return true;
        });

        const changes = candidates.map(item => ({
          id: item.id,
          patch: {
            cpaClassId:       cpaClassId,
            cpaClassName:     targetClass?.name || cpaClassId,
            linkedCpaTableId: targetClass?.linkedCpaTableId || "",
          },
        }));

        const rangeDesc = fromItemNo || toItemNo
          ? ` (items ${fromItemNo || "start"} → ${toItemNo || "end"})`
          : "";
        const filterDesc = [
          noCpaOnly      ? "no existing CPA class" : "",
          currentCpaName ? `currently "${currentCpaName}"` : "",
          keyword        ? `description contains "${keyword}"` : "",
        ].filter(Boolean).join(", ");

        const summary = `Set CPA class to **${targetClass?.name || cpaClassId}** for **${changes.length} item(s)**${rangeDesc}${filterDesc ? ` — filtered to: ${filterDesc}` : ""}.`;
        return { summary, changes, count: changes.length };
      },
    },
    {
      name: "bulk_update_field",
      description: "Bulk-update a single field (finalEstimatedQty, unit, notes) for items matching given criteria.",
      input_schema: {
        type: "object",
        properties: {
          field:      { type: "string", enum: ["finalEstimatedQty","unit","notes"], description: "Field to update" },
          value:      { type: "string", description: "New value" },
          fromItemNo: { type: "string", description: "Start of item number range" },
          toItemNo:   { type: "string", description: "End of item number range" },
          noCpaOnly:  { type: "boolean", description: "Only items with no CPA class" },
          keyword:    { type: "string", description: "Filter by description keyword" },
        },
        required: ["field","value"],
      },
      handler: async (input) => {
        const { field, value, fromItemNo, toItemNo, noCpaOnly, keyword } = input;
        const candidates = projectItems.filter(item => {
          const no = String(item.itemNo || "").trim();
          // Use numeric-aware localeCompare so A30 < A210 < A580 < A2100 is correct
          const rangeOpts = { numeric: true, sensitivity: "base" };
          if (fromItemNo && no.localeCompare(fromItemNo, undefined, rangeOpts) < 0) return false;
          if (toItemNo   && no.localeCompare(toItemNo,   undefined, rangeOpts) > 0) return false;
          if (noCpaOnly && item.cpaClassId && String(item.cpaClassId).trim() !== "") return false;
          if (keyword && !String(item.description || "").toLowerCase().includes(keyword.toLowerCase())) return false;
          return true;
        });
        const changes = candidates.map(item => ({ id: item.id, patch: { [field]: value } }));
        const summary = `Set **${field}** to **"${value}"** for **${changes.length} item(s)**.`;
        return { summary, changes, count: changes.length };
      },
    },
    {
      name: "query_soq_stats",
      description: "Answer questions about the SOQ — counts, CPA breakdown, missing CPA, tender values, or details about specific items.",
      input_schema: {
        type: "object",
        properties: { query: { type: "string", description: "What to report on" } },
        required: ["query"],
      },
      handler: async () => {
        const missing = projectItems.filter(i => !i.cpaClassId || String(i.cpaClassId).trim() === "").length;
        const summary = `SOQ has **${projectItems.length} items** · tender value **${fmtCurrency(totalTenderValue)}** · **${missing} item(s)** have no CPA class · Breakdown: ${cpaBreakdownForAi || "none"}.`;
        return { summary, changes: [], count: 0 };
      },
    },
  ];

  // Called when user clicks "Apply Changes" in the AI panel
  function handleAiChanges(changes) {
    if (!changes.length || typeof handleUpdateSoqItem !== "function") return;
    changes.forEach(({ id, patch }) => {
      if (id && patch) handleUpdateSoqItem(id, patch);
    });
  }

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════
  // ════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════
  return (
    <section style={{
      position: "relative", display: "grid", gap: 18, fontFamily: T.font.body,
      minWidth: 0, maxWidth: "100%", width: "100%", boxSizing: "border-box",
      overflowX: "clip",
    }}>
      <BlueprintGrid />

      <div style={{
        position: "relative", zIndex: 1, display: "grid", gap: 18,
        minWidth: 0, maxWidth: "100%", width: "100%", boxSizing: "border-box",
        overflowX: "clip",
      }}>

        {/* ───── PAGE HEADER ───── */}
        <Reveal delay={0} style={{ minWidth: 0, maxWidth: "100%" }}>
          <UICard padded={false} style={{ borderRadius: 14, position: "relative" }}>
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${T.color.accent} 0%, ${T.color.primary} 40%, ${T.color.primary} 100%)`,
            }} />
            <div style={{
              padding: "24px 26px",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              gap: 16, flexWrap: "wrap",
            }}>
              <div style={{ minWidth: 0, flex: "1 1 320px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  <Eyebrow>Schedule of Quantities</Eyebrow>
                  <span style={{ width: 3, height: 3, borderRadius: "50%", background: T.color.borderStrong }} />
                  <span style={{
                    fontFamily: T.font.mono, fontSize: 11, fontWeight: 600,
                    color: T.color.inkMuted, letterSpacing: "0.02em",
                  }}>{currentProjectId || "—"}</span>
                  {summary.negativBalanceCount > 0 && (
                    <>
                      <span style={{ width: 3, height: 3, borderRadius: "50%", background: T.color.borderStrong }} />
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        letterSpacing: "0.12em", textTransform: "uppercase",
                        color: T.color.danger,
                        display: "inline-flex", alignItems: "center", gap: 4,
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/></svg>
                        {summary.negativBalanceCount} over-allocated
                      </span>
                    </>
                  )}
                </div>
                <h1 style={{
                  margin: 0, fontFamily: T.font.display,
                  fontSize: 26, fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.1,
                  color: T.color.ink,
                }}>SOQ Register</h1>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: T.color.inkMuted, lineHeight: 1.5, maxWidth: 720 }}>
                  Manage the live priced schedule of quantities — final estimated qty, CPA class allocation,
                  identified savings, and remaining balance after approved VO omissions and certified work.
                </p>
              </div>
              <div style={{ flexShrink: 0, alignSelf: "center" }}>
              <AiInteractiveHelperButton
                sectionName="Schedule of Quantities (SOQ)"
                sectionContext={`SOQ register for project "${currentProjectId}". ${projectItems.length} items, tender value ${fmtCurrency(totalTenderValue)}.
CPA classes available: ${projectCpaClasses.map(c => `${c.name} (id: ${c.id})`).join(", ") || "none yet"}.
Items missing CPA: ${projectItems.filter(i => !i.cpaClassId || String(i.cpaClassId).trim() === "").length}.

FULL ITEM LIST (itemNo | description | current CPA):
${aiItemSummary}

Rules:
- noCpaOnly=true means ONLY touch items where current CPA shows as NONE
- Always check the item list above before deciding which items to update
- Never override an existing CPA class unless the user explicitly says to replace or override it`}
                suggestions={[
                  "How does CPA class affect payment certificate calculations?",
                  "Set CPA class to Earthworks for all items from A100 to A200",
                  "How many items are missing a CPA class?",
                  "What is the difference between CPA classes under GCC?",
                  "Show me the CPA class breakdown",
                ]}
                tools={aiTools}
                onApplyChanges={handleAiChanges}
                label="AI Help"
              />
              </div>
            </div>
          </UICard>
        </Reveal>

        {/* ───── KPI STRIP ───── */}
        <Reveal delay={120} style={{ minWidth: 0, maxWidth: "100%" }}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: 10,
            minWidth: 0, maxWidth: "100%",
          }}>
            <Stat
              label="SOQ Items"
              rawValue={summary.itemCount}
              format={(v) => Math.round(v)}
              value={summary.itemCount}
              accent={T.color.primary}
              delay={80}
              helper="Within current filter"
            />
            <Stat
              label="Tender Value"
              rawValue={summary.totalTenderValue}
              format={(v) => fmtCurrency(v)}
              value={fmtCurrency(summary.totalTenderValue)}
              accent={T.color.primary}
              delay={140}
              helper="Filtered total tender"
            />
            <Stat
              label="Engineer's Estimate"
              rawValue={summary.totalFinalEngineersEstimate}
              format={(v) => fmtCurrency(v)}
              value={fmtCurrency(summary.totalFinalEngineersEstimate)}
              accent={T.color.info}
              delay={200}
              helper="Final qty × tender rate"
            />
          </div>
        </Reveal>

        {/* ───── SECTION: TENDERED VALUE vs ENGINEER'S ESTIMATE ───── */}
        {sectionBreakdown.length > 0 && (
          <Reveal delay={180} style={{ minWidth: 0, maxWidth: "100%" }}>
            <UICard padded={false} style={{ borderRadius: 14 }}>
              <div style={{ padding: "20px 22px 4px" }}>
                <Eyebrow>Section Breakdown</Eyebrow>
                <h3 style={{
                  margin: "6px 0 0", fontFamily: T.font.display,
                  fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: T.color.ink,
                }}>Tendered Value vs Engineer's Estimate by Section</h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkSubtle }}>
                  Each schedule section's tendered value set against its engineer's estimate (final qty × tender rate).
                </p>
              </div>
              <div style={{
                padding: "16px 22px 22px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 10,
                minWidth: 0, maxWidth: "100%",
              }}>
                {sectionBreakdown.map((sec) => {
                  const over = sec.variance > 0; // EE above tender = overrun
                  const accent = over ? T.color.danger : T.color.success;
                  return (
                    <div key={sec.key} style={{
                      position: "relative",
                      background: T.color.surfaceAlt,
                      border: `1px solid ${T.color.border}`,
                      borderRadius: T.radius.sm,
                      padding: "14px 16px 14px 18px",
                      overflow: "hidden",
                    }}>
                      <div style={{
                        position: "absolute", top: 0, bottom: 0, left: 0, width: 3,
                        background: accent,
                      }} />
                      <div style={{
                        display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8,
                      }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{
                            fontFamily: T.font.mono, fontSize: 10, fontWeight: 700,
                            letterSpacing: "0.06em", color: T.color.inkSubtle,
                          }}>{sec.sectionNo || "—"}</div>
                          <div style={{
                            fontSize: 12, fontWeight: 700, color: T.color.ink,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }} title={sec.sectionName}>{sec.sectionName || "Unnamed section"}</div>
                        </div>
                        <span style={{
                          flexShrink: 0,
                          fontSize: 10, fontWeight: 700, fontFamily: T.font.mono,
                          color: T.color.inkSubtle,
                          background: "#ffffff",
                          border: `1px solid ${T.color.border}`,
                          padding: "2px 7px", borderRadius: 999,
                        }}>{sec.itemCount}</span>
                      </div>

                      <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                          <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
                            textTransform: "uppercase", color: T.color.inkSubtle,
                          }}>Tender</span>
                          <span style={{
                            fontFamily: T.font.mono, fontSize: 14, fontWeight: 700,
                            color: T.color.ink, fontVariantNumeric: "tabular-nums",
                          }}>{fmtCurrency(sec.tenderValue)}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                          <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
                            textTransform: "uppercase", color: T.color.inkSubtle,
                          }}>Eng. Est.</span>
                          <span style={{
                            fontFamily: T.font.mono, fontSize: 14, fontWeight: 700,
                            color: T.color.info, fontVariantNumeric: "tabular-nums",
                          }}>{fmtCurrency(sec.engineersEstimate)}</span>
                        </div>
                      </div>

                      <div style={{
                        marginTop: 10, paddingTop: 8,
                        borderTop: `1px dashed ${T.color.border}`,
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                      }}>
                        <span style={{ fontSize: 10, color: T.color.inkMuted }}>
                          {over ? "EE over tender" : "Saving vs tender"}
                        </span>
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          fontFamily: T.font.mono, fontSize: 12, fontWeight: 700,
                          color: accent, fontVariantNumeric: "tabular-nums",
                        }}>
                          {over ? "▲" : "▼"} {fmtCurrency(Math.abs(sec.variance))}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </UICard>
          </Reveal>
        )}

        {/* ───── SAVINGS TILES ───── */}
        <Reveal delay={240} style={{ minWidth: 0, maxWidth: "100%" }}>
          <UICard padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 4px" }}>
              <Eyebrow color={T.color.success}>Identified Savings</Eyebrow>
              <h3 style={{
                margin: "6px 0 0", fontFamily: T.font.display,
                fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: T.color.ink,
              }}>Savings Position</h3>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkSubtle }}>
                Track contractor-identified savings, what's been formally allocated via VO omission, and what's still on the table.
              </p>
            </div>
            <div style={{
              padding: "16px 22px 22px",
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 10,
              minWidth: 0, maxWidth: "100%",
            }}>
              <div style={{
                position: "relative", background: T.color.surfaceAlt,
                border: `1px solid ${T.color.border}`, borderRadius: T.radius.sm,
                padding: "14px 16px", overflow: "hidden",
              }}>
                <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 3, background: T.color.success }} />
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>
                  Total Identified
                </div>
                <div style={{ marginTop: 6, fontFamily: T.font.mono, fontSize: 16, fontWeight: 700, color: T.color.ink, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>
                  {fmtCurrency(summary.totalSavingsValue)}
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: T.color.inkMuted }}>
                  <span style={{ fontFamily: T.font.mono, fontWeight: 600 }}>{summary.savingsCount}</span> item{summary.savingsCount !== 1 ? "s" : ""} flagged
                </div>
              </div>
              <div style={{
                position: "relative", background: T.color.surfaceAlt,
                border: `1px solid ${T.color.border}`, borderRadius: T.radius.sm,
                padding: "14px 16px", overflow: "hidden",
              }}>
                <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 3, background: T.color.warning }} />
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>
                  Allocated via VO
                </div>
                <div style={{ marginTop: 6, fontFamily: T.font.mono, fontSize: 16, fontWeight: 700, color: T.color.warning, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums" }}>
                  {fmtCurrency(summary.savingsAllocated)}
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: T.color.inkMuted }}>
                  Approved VO omissions
                </div>
              </div>
              <div style={{
                position: "relative", background: T.color.surfaceAlt,
                border: `1px solid ${T.color.border}`, borderRadius: T.radius.sm,
                padding: "14px 16px", overflow: "hidden",
              }}>
                <div style={{
                  position: "absolute", top: 0, bottom: 0, left: 0, width: 3,
                  background: summary.savingsBalance < 0 ? T.color.danger : T.color.accent,
                }} />
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>
                  Balance
                </div>
                <div style={{
                  marginTop: 6, fontFamily: T.font.mono, fontSize: 16, fontWeight: 700,
                  color: summary.savingsBalance < 0 ? T.color.danger : T.color.accent,
                  letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums",
                }}>
                  {fmtCurrency(summary.savingsBalance)}
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: T.color.inkMuted }}>
                  {summary.savingsBalance < 0 ? "Over-allocated" : "Still to be allocated"}
                </div>
              </div>
            </div>
          </UICard>
        </Reveal>

        {/* Negative balance alert */}
        {summary.negativBalanceCount > 0 && (
          <Reveal delay={200} style={{ minWidth: 0, maxWidth: "100%" }}>
            <div style={{
              padding: "12px 16px", borderRadius: T.radius.md,
              background: T.color.dangerSoft, color: T.color.danger,
              border: `1px solid ${T.color.dangerBorder}`,
              fontSize: 12, fontWeight: 600,
              display: "flex", gap: 12, alignItems: "center",
            }}>
              <span style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "rgba(199,59,42,0.15)",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </span>
              <span>
                <strong style={{ fontFamily: T.font.mono }}>{summary.negativBalanceCount}</strong> SOQ
                item{summary.negativBalanceCount !== 1 ? "s have" : " has"} a negative remaining quantity —
                over-allocation detected. Review VO omission quantities to bring them back within available balance.
              </span>
            </div>
          </Reveal>
        )}

        {/* ───── CPA CLASS ALLOCATION ───── */}
        <Reveal delay={280} style={{ minWidth: 0, maxWidth: "100%" }}>
          <UICard padded={false} style={{ borderRadius: 14, minWidth: 0, maxWidth: "100%" }}>
            <div style={{
              padding: "20px 22px 4px",
              display: "flex", alignItems: "flex-end", justifyContent: "space-between",
              gap: 16, flexWrap: "wrap",
            }}>
              <div style={{ minWidth: 0, flex: "1 1 280px" }}>
                <Eyebrow color={T.color.info}>Classification</Eyebrow>
                <h3 style={{
                  margin: "6px 0 0", fontFamily: T.font.display,
                  fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: T.color.ink,
                }}>CPA Class Allocation</h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkSubtle }}>
                  Assign SOQ items to CPA classes for contract price adjustment calculations.
                </p>
              </div>
              <div style={{ flexShrink: 0, alignSelf: "flex-end" }}>
              <AiInteractiveHelperButton
                sectionName="CPA Class Allocation"
                sectionContext={`Managing CPA classes for SOQ items. Available classes: ${projectCpaClasses.map(c => `${c.name} (id: ${c.id})`).join(", ") || "none yet"}. ${selectedRowIds.length} items currently selected.

FULL ITEM LIST (itemNo | description | current CPA):
${aiItemSummary}

Rules: noCpaOnly=true means ONLY touch items where current CPA shows as NONE. Never override an existing CPA class unless explicitly told to.`}
                suggestions={[
                  "Bulk set selected items to a specific CPA class",
                  "What CPA table should earthworks items use?",
                  "Which items have no CPA class assigned?",
                ]}
                tools={aiTools}
                onApplyChanges={handleAiChanges}
                label="AI Help"
              />
              </div>
            </div>

            <div style={{ padding: "16px 22px 22px" }}>
              {/* Create new class */}
              <div style={{
                display: "grid", gridTemplateColumns: "minmax(200px, 1fr) minmax(220px, 1fr) auto",
                gap: 10, alignItems: "end",
              }}>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: T.color.inkSubtle,
                  }}>New CPA Class Name</span>
                  <UIInput
                    value={newCpaClassName}
                    onChange={(e) => setNewCpaClassName(e.target.value)}
                    placeholder="e.g. Earthworks, Pavements, Stormwater" />
                </div>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: T.color.inkSubtle,
                  }}>Linked CPA Table / Formula</span>
                  <UISelect value={newCpaTableLink} onChange={(e) => setNewCpaTableLink(e.target.value)}>
                    <option value="">Select CPA table / formula…</option>
                    {cpaTableOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </UISelect>
                </div>
                <UIButton
                  variant="primary"
                  onClick={handleAddCpaClass}
                  disabled={!newCpaClassName.trim() || typeof handleCreateCpaClass !== "function"}
                  icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
                >
                  Add Class
                </UIButton>
              </div>

              {typeof handleCreateCpaClass !== "function" && (
                <div style={{
                  marginTop: 12, padding: "10px 14px", borderRadius: T.radius.sm,
                  background: T.color.warningSoft, color: T.color.warning,
                  border: `1px solid ${T.color.warningBorder}`,
                  fontSize: 12, fontWeight: 600,
                  display: "flex", gap: 8, alignItems: "center",
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  CPA class saving not connected. Wire <code style={{ fontFamily: T.font.mono }}>handleCreateCpaClass</code> from App.js.
                </div>
              )}

              {/* Existing classes table */}
              {projectCpaClasses.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <UITable>
                    <thead>
                      <tr>
                        <UITh>CPA Class</UITh>
                        <UITh>Linked Table / Formula</UITh>
                        <UITh align="right">Items</UITh>
                        <UITh>Status</UITh>
                        <UITh align="right"></UITh>
                      </tr>
                    </thead>
                    <tbody>
                      {projectCpaClasses.map((cls) => {
                        const assignedCount = projectItems.filter((i) => String(i.cpaClassId) === String(cls.id)).length;
                        return (
                          <tr key={cls.id}>
                            <UITd weight={700}>{cls.name || cls.id}</UITd>
                            <UITd mono>{cls.linkedCpaTableId || <span style={{ color: T.color.inkSubtle }}>—</span>}</UITd>
                            <UITd align="right">
                              <UIBadge variant={assignedCount > 0 ? "info" : "default"} size="sm">
                                {assignedCount} item{assignedCount !== 1 ? "s" : ""}
                              </UIBadge>
                            </UITd>
                            <UITd>
                              <UIBadge variant={cls.active !== false ? "success" : "default"} size="sm">
                                {cls.active !== false ? "Active" : "Inactive"}
                              </UIBadge>
                            </UITd>
                            <UITd align="right">
                              {typeof handleDeleteCpaClass === "function" && (
                                <UIButton
                                  variant="danger"
                                  size="sm"
                                  onClick={() => {
                                    if (window.confirm(`Delete CPA class "${cls.name}"? This cannot be undone.`)) {
                                      handleDeleteCpaClass(cls.id);
                                    }
                                  }}>
                                  Delete
                                </UIButton>
                              )}
                            </UITd>
                          </tr>
                        );
                      })}
                    </tbody>
                  </UITable>
                </div>
              )}

              {/* Bulk apply */}
              <div style={{
                marginTop: 16,
                padding: "14px 16px", borderRadius: T.radius.sm,
                background: T.color.primarySoft,
                border: `1px solid #C0CDD8`,
                display: "grid", gridTemplateColumns: "minmax(220px, 1fr) auto",
                gap: 12, alignItems: "end",
              }}>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
                    textTransform: "uppercase", color: T.color.primary,
                  }}>Bulk Apply CPA Class</span>
                  <UISelect value={bulkCpaClassId} onChange={(e) => setBulkCpaClassId(e.target.value)}>
                    <option value="">Select class to apply…</option>
                    {projectCpaClasses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </UISelect>
                </div>
                <UIButton
                  variant="primary"
                  onClick={handleApplyBulkCpa}
                  disabled={!bulkCpaClassId || !selectedRowIds.length}
                >
                  Apply to {selectedRowIds.length} Selected
                </UIButton>
              </div>
              {!selectedRowIds.length && (
                <p style={{ margin: "6px 0 0", fontSize: 11, color: T.color.inkSubtle, textAlign: "right" }}>
                  Select rows in the register below first.
                </p>
              )}
            </div>
          </UICard>
        </Reveal>

        {/* ───── MAIN REGISTER TABLE ───── */}
        <Reveal delay={340} style={{ minWidth: 0, maxWidth: "100%" }}>
          <UICard padded={false} style={{ borderRadius: 14, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            <div style={{
              padding: "20px 22px 16px",
              display: "flex", alignItems: "flex-end", justifyContent: "space-between",
              gap: 16, flexWrap: "wrap",
            }}>
              <div>
                <Eyebrow>Register</Eyebrow>
                <h3 style={{
                  margin: "6px 0 0", fontFamily: T.font.display,
                  fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: T.color.ink,
                }}>SOQ Items</h3>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.color.inkSubtle }}>
                  Showing <strong style={{ fontFamily: T.font.mono, color: T.color.ink }}>{filteredRows.length}</strong> of{" "}
                  <strong style={{ fontFamily: T.font.mono, color: T.color.ink }}>{projectItems.length}</strong> items
                  {selectedRowIds.length > 0 && (
                    <> · <strong style={{ fontFamily: T.font.mono, color: T.color.accent }}>{selectedRowIds.length}</strong> selected</>
                  )}
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <UIButton
                  variant="subtle"
                  onClick={handleDownloadClaimTemplate}
                  disabled={templateBusy}
                  title="Export this project's SOQ as a monthly-claim Excel for the contractor to fill in (item numbers pre-matched to your SOQ)"
                  icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>}
                >
                  {templateBusy ? "Building…" : "Download Claim Template"}
                </UIButton>
                <UIButton
                  variant="subtle"
                  onClick={() => setShowImportModal(true)}
                  icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>}
                >
                  Import Final Qty
                </UIButton>
              </div>
            </div>

            <div style={{ padding: "0 22px 22px", minWidth: 0, maxWidth: "100%" }}>

              {/* Filter bar */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: 10, marginBottom: 16,
                minWidth: 0, maxWidth: "100%",
              }}>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>Search</span>
                  <UIInput
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Item no, description, CPA class…" />
                </div>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>CPA Class</span>
                  <UISelect value={cpaClassFilter} onChange={(e) => setCpaClassFilter(e.target.value)}>
                    <option value="All">All classes</option>
                    <option value="__unassigned__">Unassigned</option>
                    {projectCpaClasses.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </UISelect>
                </div>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>Savings</span>
                  <UISelect value={savingsFilter} onChange={(e) => setSavingsFilter(e.target.value)}
                    style={savingsFilter !== "All" ? {
                      borderColor: T.color.success,
                      background: T.color.successSoft,
                      color: T.color.success, fontWeight: 600,
                    } : undefined}>
                    <option value="All">All items</option>
                    <option value="savings">Identified savings ✓</option>
                    <option value="non-savings">Non-savings only</option>
                  </UISelect>
                </div>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>Section</span>
                  <UISelect value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
                    {sectionOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </UISelect>
                </div>
                <div style={{ display: "grid", gap: 4 }}>
                  <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.color.inkSubtle }}>Sort By</span>
                  <UISelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                    <option value="itemNo_asc">Item No (A → Z)</option>
                    <option value="itemNo_desc">Item No (Z → A)</option>
                    <option value="value_desc">Amount (High → Low)</option>
                    <option value="value_asc">Amount (Low → High)</option>
                    <option value="remaining_qty_desc">Remaining (High → Low)</option>
                    <option value="remaining_qty_asc">Remaining (Low → High)</option>
                  </UISelect>
                </div>
              </div>

              {/* Subtotal isolation banner */}
              {isolatedSubtotalId && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 14px",
                  background: `linear-gradient(135deg, ${T.color.accentSoft || "#dbeafe"} 0%, ${T.color.surface} 100%)`,
                  border: `1px solid ${T.color.accent}`,
                  borderRadius: 12,
                  marginBottom: 12,
                }}>
                  <span style={{
                    fontSize: 16, lineHeight: 1,
                    width: 30, height: 30, borderRadius: 8,
                    background: T.color.accent, color: "#ffffff",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800,
                  }}>🔍</span>
                  <div style={{ flex: 1, display: "grid", gap: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.color.inkMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Isolated subtotal group
                    </span>
                    <strong style={{ fontSize: 14, color: T.color.ink }}>
                      {isolatedGroupLabel}
                      <span style={{ marginLeft: 8, color: T.color.inkMuted, fontWeight: 600, fontSize: 12 }}>
                        · {displayGroups[0]?.rowIds.length || 0} item{(displayGroups[0]?.rowIds.length || 0) === 1 ? "" : "s"}
                      </span>
                    </strong>
                    <span style={{ fontSize: 11, color: T.color.inkMuted, fontStyle: "italic" }}>
                      View-only — the summary totals above continue to show all visible rows
                    </span>
                  </div>
                  <button
                    onClick={() => setIsolatedSubtotalId(null)}
                    style={{
                      border: `1px solid ${T.color.border}`,
                      background: "#ffffff",
                      color: T.color.ink,
                      padding: "6px 12px",
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >✕ Clear isolation</button>
                </div>
              )}

              {/* Subtotal controls (only when at least one marker exists) */}
              {subtotalMarkers.length > 0 && !isolatedSubtotalId && (
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                  padding: "8px 12px",
                  background: T.color.surface,
                  border: `1px solid ${T.color.border}`,
                  borderRadius: 10,
                  marginBottom: 12,
                }}>
                  <span style={{ fontSize: 12, color: T.color.inkMuted, fontWeight: 600 }}>
                    {subtotalMarkers.length} subtotal{subtotalMarkers.length === 1 ? "" : "s"} placed · hover any row's first column to add another
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={applyAllSections}
                      title="Stamp every named section onto its items — backfills existing items and picks up newly-added ones"
                      style={{
                        border: `1px solid ${T.color.accent}`, background: T.color.accent,
                        color: "#ffffff", padding: "4px 10px", borderRadius: 6,
                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                      }}
                    >Apply sections to items</button>
                    <button
                      onClick={clearAllSubtotals}
                      style={{
                        border: `1px solid ${T.color.border}`, background: "transparent",
                        color: T.color.inkMuted, padding: "4px 10px", borderRadius: 6,
                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                      }}
                    >Remove all</button>
                  </div>
                </div>
              )}

              {/* Table */}
              {filteredRows.length === 0 ? (
                <Empty
                  title="No SOQ items match the filter"
                  message="Adjust the search / CPA class / section filters above, or import the SOQ schedule first to seed the register."
                />
              ) : (
                <div style={{
                  width: "100%", maxWidth: "100%", minWidth: 0,
                  overflowX: "auto", overflowY: "visible",
                  WebkitOverflowScrolling: "touch",
                }}>
                  <div style={{ minWidth: 1600 }}>
                <UITable>
                  <thead>
                    <tr>
                      <UITh style={{ width: 36 }}>
                        <UICheckbox checked={allSelected} onChange={toggleAll} />
                      </UITh>
                      <UITh>Item No</UITh>
                      <UITh>CPA Class</UITh>
                      <UITh>Payment Ref</UITh>
                      <UITh>Description</UITh>
                      <UITh>Unit</UITh>
                      <UITh align="right">Tender Qty</UITh>
                      <UITh align="right">Final Est. Qty</UITh>
                      <UITh align="right">Engineer's Est.</UITh>
                      <UITh align="right">VO Alloc.</UITh>
                      <UITh align="right">Certified</UITh>
                      <UITh align="right">Remaining</UITh>
                      <UITh align="right">Tender Rate</UITh>
                      <UITh align="right">Tender Amount</UITh>
                      <UITh>Section</UITh>
                      <UITh>Used By</UITh>
                      <UITh align="center">Saving</UITh>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const rowById = {};
                      filteredRows.forEach((r) => { rowById[r.id] = r; });
                      const TOTAL_COLS = 17;
                      const out = [];

                      displayGroups.forEach((group, gIdx) => {
                        const groupMarker = subtotalMarkers.find((m) => m.id === group.markerRowId);
                        const groupSectionNo = String(groupMarker?.sectionNo || "").trim();
                        const groupSectionName = String(groupMarker?.sectionName || "").trim();
                        if (groupSectionNo || groupSectionName) {
                          out.push(
                            <tr key={`section-head-${group.markerRowId || gIdx}`} style={{ background: T.color.accentSoft || "#eef2ff" }}>
                              <td colSpan={TOTAL_COLS} style={{ padding: "7px 14px", fontWeight: 800, fontSize: 12.5, color: T.color.ink }}>
                                <span style={{ opacity: 0.55, marginRight: 8 }}>📂</span>
                                {[groupSectionNo, groupSectionName].filter(Boolean).join(" — ")}
                              </td>
                            </tr>
                          );
                        }
                        group.rowIds.forEach((rid) => {
                          const row = rowById[rid];
                          if (!row) return;
                          const isNegative  = round2(row.remainingQty) < 0;
                          const hasUsage    = row.omissionAllocations.length > 0 || row.certificateConsumptions.length > 0;
                          const isExpanded  = expandedRows[row.id];
                          const usageCount  = row.omissionAllocations.length + row.certificateConsumptions.length;
                          const isSelected  = selectedRowIds.includes(row.id);
                          const isHovered   = hoveredRowId === row.id;
                          const isMarker    = subtotalMarkers.some((m) => m.id === row.id);

                          out.push(
                        <tr
                          key={row.id}
                          onMouseEnter={() => setHoveredRowId(row.id)}
                          onMouseLeave={() => setHoveredRowId((v) => (v === row.id ? null : v))}
                          style={{
                          background: isMarker     ? (T.color.accentSoft || "#dbeafe")
                                    : isNegative   ? T.color.dangerSoft
                                    : isSelected   ? T.color.accentSoft
                                    : undefined,
                          borderLeft: isMarker   ? `3px solid ${T.color.accent}`
                                    : isNegative ? `3px solid ${T.color.danger}`
                                    : isSelected ? `3px solid ${T.color.accent}`
                                    : "3px solid transparent",
                        }}>
                          {/* Checkbox + floating subtotal-add button */}
                          <UITd style={{ position: "relative" }}>
                            <UICheckbox checked={isSelected} onChange={() => toggleOne(row.id)} />
                            {!isMarker && (isHovered || hoveredRowId === row.id) && (
                              <button
                                type="button"
                                title="Add subtotal row below this item"
                                onClick={(e) => { e.stopPropagation(); addSubtotalAfter(row.id); }}
                                style={{
                                  position: "absolute",
                                  left: -8,
                                  top: "100%",
                                  transform: "translateY(-50%)",
                                  width: 22,
                                  height: 22,
                                  borderRadius: "50%",
                                  border: `2px solid ${T.color.surface}`,
                                  background: T.color.accent,
                                  color: "#ffffff",
                                  fontSize: 16,
                                  lineHeight: 1,
                                  fontWeight: 800,
                                  cursor: "pointer",
                                  boxShadow: "0 4px 10px rgba(15,23,42,0.25)",
                                  padding: 0,
                                  zIndex: 5,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}
                              >+</button>
                            )}
                          </UITd>

                          {/* Item No */}
                          <UITd mono weight={700}>{row.itemNo || "—"}</UITd>

                          {/* CPA Class selector */}
                          <UITd style={{ minWidth: 160 }}>
                            <UISelect
                              value={row.cpaClassId || ""}
                              onChange={(e) => handleRowCpaChange(row, e.target.value)}
                              size="sm"
                            >
                              <option value="">Unassigned</option>
                              {projectCpaClasses.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </UISelect>
                          </UITd>

                          <UITd mono>{row.paymentRef || <span style={{ color: T.color.inkSubtle }}>—</span>}</UITd>

                          {/* Description */}
                          <UITd style={{ maxWidth: 240 }}>
                            <span title={row.description || ""} style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}>{row.description || "—"}</span>
                          </UITd>

                          <UITd mono>{row.unit || "—"}</UITd>

                          {/* Tender Qty */}
                          <UITd align="right" mono>{round2(row.tenderQty)}</UITd>

                          {/* Final estimated qty — editable */}
                          <UITd style={{ minWidth: 110 }}>
                            <UIInput
                              type="number"
                              size="sm"
                              value={estimatedQtyByItem[row.id] == null || estimatedQtyByItem[row.id] === "" ? "0" : estimatedQtyByItem[row.id]}
                              onChange={(e) => handleEstimatedQtyChange(row.id, e.target.value)}
                              onBlur={() => handleEstimatedQtyBlur(row)}
                              placeholder="0"
                              style={{ textAlign: "right" }}
                            />
                          </UITd>

                          {/* Engineer's estimate */}
                          <UITd align="right" mono weight={600}
                            style={{ color: row.finalEngineersEstimateAmount > 0 ? T.color.info : T.color.inkSubtle }}>
                            {fmtCurrency(row.finalEngineersEstimateAmount)}
                          </UITd>

                          {/* VO allocated qty */}
                          <UITd align="right" mono
                            style={{ color: row.allocatedVoQty > 0 ? T.color.warning : T.color.inkSubtle }}>
                            {round2(row.allocatedVoQty)}
                          </UITd>

                          {/* Certified qty */}
                          <UITd align="right" mono
                            style={{ color: row.certifiedQty > 0 ? T.color.success : T.color.inkSubtle }}>
                            {round2(row.certifiedQty)}
                          </UITd>

                          {/* Remaining qty */}
                          <UITd align="right" mono weight={isNegative ? 700 : 500}
                            style={{ color: isNegative ? T.color.danger : T.color.ink }}>
                            {round2(row.remainingQty)}{isNegative && " ⚠"}
                          </UITd>

                          <UITd align="right" mono style={{ color: T.color.inkMuted }}>{fmtCurrency(row.tenderRate)}</UITd>
                          <UITd align="right" mono weight={600}>{fmtCurrency(row.tenderAmount)}</UITd>

                          {/* Section */}
                          <UITd style={{ whiteSpace: "nowrap", color: T.color.inkMuted, fontSize: 11 }}>
                            {row.sectionNo ? `${row.sectionNo} — ${row.sectionName || ""}` : "—"}
                          </UITd>

                          {/* Used By — expandable */}
                          <UITd style={{ minWidth: 160 }}>
                            {!hasUsage ? (
                              <span style={{ color: T.color.inkSubtle }}>—</span>
                            ) : (
                              <div>
                                <button
                                  onClick={() => toggleExpand(row.id)}
                                  style={{
                                    background: "none", border: "none", cursor: "pointer",
                                    color: T.color.primary, fontSize: 11, fontWeight: 700,
                                    padding: 0, fontFamily: "inherit",
                                    letterSpacing: "0.04em", textTransform: "uppercase",
                                  }}>
                                  {isExpanded ? "▲ Hide" : `▼ ${usageCount} usage${usageCount !== 1 ? "s" : ""}`}
                                </button>
                                {isExpanded && (
                                  <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
                                    {row.omissionAllocations.map((e) => (
                                      <div key={`vo-${e.variationId}`} style={{
                                        fontSize: 10, padding: "4px 8px", borderRadius: 4,
                                        background: T.color.warningSoft,
                                        color: T.color.warning,
                                        border: `1px solid ${T.color.warningBorder}`,
                                        fontWeight: 600,
                                        fontFamily: T.font.mono,
                                      }}>
                                        VO {e.variationNo}: {round2(e.omitQty)} omitted
                                      </div>
                                    ))}
                                    {row.certificateConsumptions.map((e) => (
                                      <div key={`cert-${e.certificateId}`} style={{
                                        fontSize: 10, padding: "4px 8px", borderRadius: 4,
                                        background: T.color.successSoft,
                                        color: T.color.success,
                                        border: `1px solid ${T.color.successBorder}`,
                                        fontWeight: 600,
                                        fontFamily: T.font.mono,
                                      }}>
                                        {e.certificateNo}: {round2(e.thisMonthQty)} certified
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </UITd>

                          {/* Identified Saving */}
                          <UITd align="center">
                            <UICheckbox
                              checked={!!row.identifiedSaving}
                              onChange={() => handleToggleSaving(row)}
                            />
                          </UITd>
                        </tr>
                          );
                        });

                        if (group.isClosed) {
                          const marker = subtotalMarkers.find((m) => m.id === group.markerRowId);
                          const isThisIsolated = isolatedSubtotalId === group.markerRowId;
                          out.push(
                            <tr
                              key={`subtotal-${group.markerRowId}`}
                              style={{
                                background: `linear-gradient(180deg, ${T.color.accentSoft || "#dbeafe"} 0%, ${T.color.surface} 100%)`,
                                borderTop: `2px solid ${T.color.accent}`,
                                borderBottom: `2px solid ${T.color.accent}`,
                              }}
                            >
                              <td colSpan={TOTAL_COLS} style={{ padding: "10px 14px" }}>
                                <div style={{
                                  display: "flex", alignItems: "center", gap: 16,
                                  flexWrap: "wrap",
                                }}>
                                  <span style={{
                                    width: 28, height: 28, borderRadius: 8,
                                    background: T.color.accent, color: "#ffffff",
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    fontWeight: 800, fontSize: 14, fontFamily: T.font.mono,
                                    flexShrink: 0,
                                  }}>Σ</span>

                                  <input
                                    type="text"
                                    value={marker?.sectionNo || ""}
                                    onChange={(e) => setMarkerSection(group.markerRowId, { sectionNo: e.target.value })}
                                    onBlur={() => stampGroupSection(group)}
                                    title="Section number — applied to every item in this group"
                                    style={{
                                      border: "none", background: "transparent",
                                      fontSize: 14, fontWeight: 800, color: T.color.ink,
                                      fontFamily: T.font.mono,
                                      width: 48, textAlign: "center",
                                      padding: "4px 0",
                                      borderBottom: `1px dashed ${T.color.border}`,
                                      outline: "none",
                                    }}
                                    placeholder="No."
                                  />
                                  <input
                                    type="text"
                                    value={marker?.sectionName || ""}
                                    onChange={(e) => setMarkerSection(group.markerRowId, { sectionName: e.target.value })}
                                    onBlur={() => stampGroupSection(group)}
                                    title="Section name — applied to every item in this group on blur"
                                    style={{
                                      border: "none", background: "transparent",
                                      fontSize: 14, fontWeight: 800, color: T.color.ink,
                                      fontFamily: T.font.body,
                                      minWidth: 140, maxWidth: 240,
                                      padding: "4px 0",
                                      borderBottom: `1px dashed ${T.color.border}`,
                                      outline: "none",
                                    }}
                                    placeholder={`Section name (group ${gIdx + 1})`}
                                  />

                                  <span style={{
                                    fontSize: 11, color: T.color.inkMuted, fontWeight: 700,
                                    background: "#ffffff",
                                    padding: "3px 8px", borderRadius: 999,
                                    border: `1px solid ${T.color.border}`,
                                  }}>
                                    {group.rowIds.length} item{group.rowIds.length === 1 ? "" : "s"}
                                  </span>

                                  <div style={{ display: "flex", gap: 16, flex: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                                    <SubtotalAmount
                                      label="Tender"
                                      value={group.tenderAmount}
                                    />
                                    <SubtotalAmount
                                      label="Engineer's Est."
                                      value={group.engineersEstimate}
                                    />
                                    <SubtotalAmount
                                      label="Certified"
                                      value={group.certifiedAmount}
                                      accent={T.color.success}
                                    />
                                  </div>

                                  <div style={{ display: "flex", gap: 6 }}>
                                    <button
                                      onClick={() => isolateSubtotal(group.markerRowId)}
                                      title={isThisIsolated ? "Clear isolation" : "Isolate this subtotal group"}
                                      style={{
                                        border: `1px solid ${isThisIsolated ? T.color.accent : T.color.border}`,
                                        background: isThisIsolated ? T.color.accent : "#ffffff",
                                        color: isThisIsolated ? "#ffffff" : T.color.ink,
                                        padding: "5px 10px", borderRadius: 8,
                                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                                      }}
                                    >🔍 {isThisIsolated ? "Isolated" : "Isolate"}</button>
                                    <button
                                      onClick={() => removeSubtotal(group.markerRowId)}
                                      title="Remove this subtotal marker"
                                      style={{
                                        border: `1px solid ${T.color.border}`,
                                        background: "#ffffff",
                                        color: T.color.danger,
                                        padding: "5px 8px", borderRadius: 8,
                                        fontSize: 11, fontWeight: 700, cursor: "pointer",
                                      }}
                                    >✕</button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      });

                      return out;
                    })()}
                  </tbody>
                </UITable>
                  </div>
                </div>
              )}
            </div>
          </UICard>
        </Reveal>

        {/* ───── IMPORT MODAL (preserved untouched) ───── */}
        {showImportModal && (
          <FinalQtyImportModal
            projectItems={projectItems}
            onClose={() => setShowImportModal(false)}
            onApply={(updates) => {
              handleImportApply(updates);
              setShowImportModal(false);
            }}
          />
        )}

      </div>
    </section>
  );
}
export default React.memo(SOQRegisterSection);